import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE as P } from "../tokens";

// Small ⓘ affordance + bottom sheet explaining what the Signal score is
// (and is not). Required by App Store Guideline 1.4.1 — health-adjacent
// scores must disclose their basis. Mounted on every surface that
// renders the score: SignalScreen, Home, and the pre-reveal dashboard.
//
// Deliberately quiet: a muted inline button, a calm sheet, no alarm
// styling. The copy stays inside Haiven's non-clinical guardrails
// (conditions, not outcomes; no diagnosis language).

const SHEET_STYLES = `
  @keyframes sigInfoFade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes sigInfoRise { from { transform: translateY(24px); opacity: 0; }
                           to   { transform: translateY(0);    opacity: 1; } }
  .sig-info-trigger {
    display: inline-flex; align-items: center; gap: 5px;
    background: none; border: none; cursor: pointer; padding: 6px 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px; font-weight: 400; color: ${P.textFaint};
    transition: color 0.15s;
  }
  .sig-info-trigger:hover { color: ${P.primary}; }
  .sig-info-backdrop {
    position: fixed; inset: 0; z-index: 1200;
    background: rgba(28,24,20,0.35);
    animation: sigInfoFade 0.2s ease;
    display: flex; align-items: flex-end; justify-content: center;
  }
  .sig-info-sheet {
    width: 100%; max-width: 420px;
    max-height: 82vh; overflow-y: auto;
    background: ${P.surface};
    border-radius: 22px 22px 0 0;
    padding: 26px 24px calc(24px + env(safe-area-inset-bottom));
    box-shadow: 0 -8px 40px rgba(28,24,20,0.18);
    animation: sigInfoRise 0.25s ease;
    font-family: 'DM Sans', sans-serif;
  }
`;

function InfoGlyph({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="9.2" />
      <path d="M12 11v5.2" />
      <circle cx="12" cy="7.8" r="0.4" fill="currentColor" />
    </svg>
  );
}

export default function SignalInfoButton({ style }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <style>{SHEET_STYLES}</style>
      <button
        type="button"
        className="sig-info-trigger"
        style={style}
        aria-label="About this score"
        onClick={() => setOpen(true)}
      >
        <InfoGlyph />
        About this score
      </button>

      {open && (
        <div
          className="sig-info-backdrop"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="About your Signal score"
        >
          <div className="sig-info-sheet" onClick={(e) => e.stopPropagation()}>
            <div style={{
              fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
              color: P.primaryMid, fontWeight: 500, marginBottom: 12,
            }}>
              About your Signal score
            </div>
            <p style={{
              fontSize: 13.5, color: P.text, fontWeight: 300,
              lineHeight: 1.7, margin: "0 0 10px",
            }}>
              Signal scores reflect patterns in your self-reported check-ins
              across Body, Mind, and Behavior — plus wearable data when you
              connect a device. They describe conditions in your own data,
              not outcomes.
            </p>
            <p style={{
              fontSize: 13.5, color: P.textMid, fontWeight: 300,
              lineHeight: 1.7, margin: "0 0 18px",
            }}>
              They are not a medical assessment or diagnosis, and Haiven is
              not a medical device. Haiven does not diagnose, treat, or
              provide psychotherapy.
            </p>
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/privacy"); }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                padding: 0, fontFamily: "'DM Sans', sans-serif",
                fontSize: 12.5, color: P.primary, fontWeight: 400,
                marginBottom: 20, display: "block",
              }}
            >
              Learn more in our Privacy Policy →
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                width: "100%", padding: 15, borderRadius: 14,
                background: P.primary, color: P.bg, border: "none",
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                fontSize: 13.5, fontWeight: 500,
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
